import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Tooltip,
  Badge,
  Divider,
  Rating,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Restaurant as RestaurantIcon,
  LocalOffer as TagIcon,
  Star as StarIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import mealsApi from "../../api/meals";
import Layout from "../../components/layout/Layout";
import MealDetailModal from "./MealDetailModal";

interface Meal {
  _id: string;
  name: string;
  description: string;
  nutritionalInfo: {
    calories?: number;
    protein?: number;
    carbs?: number;
    fats?: number;
    fibre?: number;
    magnesium?: number;
    vitaminsAndMinerals?: string[];
  };
  allergens: string[];
  ingredients: string[];
  portionSize: string;
  category: string;
  serialNumber: string;
  imageUrl: string;
  rating: number;
  noOfRatings: number;
}

// Helper function to convert serial number to number for sorting
const getSerialNumberAsNumber = (serialNumber: string): number => {
  if (!serialNumber) return 0;
  const num = parseInt(serialNumber.replace(/\D/g, ''), 10);
  return isNaN(num) ? 0 : num;
};

// Helper function to format nutritional info
const formatNutritionalInfo = (nutritionalInfo: any) => {
  const info = [];
  if (nutritionalInfo.calories) info.push(`${nutritionalInfo.calories} cal`);
  if (nutritionalInfo.protein) info.push(`${nutritionalInfo.protein}g protein`);
  if (nutritionalInfo.carbs) info.push(`${nutritionalInfo.carbs}g carbs`);
  if (nutritionalInfo.fats) info.push(`${nutritionalInfo.fats}g fats`);
  return info.join(' • ');
};

const Meals: React.FC = () => {
  const [meals, setMeals] = useState<Meal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  
  const [selectedMealId, setSelectedMealId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [category, setCategory] = useState<string>("");
  const [sortBy, setSortBy] = useState<string>("serialNumber");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Handle search input with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchMeals();
  }, [debouncedSearch, category, sortBy, sortOrder]);

  const fetchMeals = async () => {
    setLoading(true);
    try {
      const data = await mealsApi.getMeals({
        search: debouncedSearch,
        category,
        sortBy,
        sortOrder,
      });

      // Sort by serial number if that's the sort field
      let sortedMeals = data.meals;
      if (sortBy === "serialNumber") {
        sortedMeals = data.meals.sort((a: Meal, b: Meal) => {
          const aNum = getSerialNumberAsNumber(a.serialNumber);
          const bNum = getSerialNumberAsNumber(b.serialNumber);
          return sortOrder === "asc" ? aNum - bNum : bNum - aNum;
        });
      }

      setMeals(sortedMeals);
      setTotal(data.pagination.total);
    } catch (err: any) {
      setError(err.response?.data?.message || "Error fetching meals");
    } finally {
      setLoading(false);
    }
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setDebouncedSearch("");
    setCategory("");
    setSortBy("serialNumber");
    setSortOrder("asc");
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, "primary" | "secondary" | "success" | "warning" | "error" | "default"> = {
      "breakfast": "primary",
      "lunch": "secondary",
      "dinner": "success",
      "snack": "warning",
      "dessert": "error",
    };
    return colors[category?.toLowerCase()] || "default";
  };

  const filteredMeals = meals.filter(meal =>
    searchTerm === "" ||
    meal.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    meal.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    meal.serialNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    meal.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout>
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Meals
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchMeals}
              disabled={loading}
            >
              Refresh
            </Button>
            {/* Add Meal button removed as per requirements */}
          </Box>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <FilterIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Filters</Typography>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 2 }}>
            <TextField
              label="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
              size="small"
              placeholder="Search by name, description, serial number, or category"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end">
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {/* <TextField
              select
              label="Category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              fullWidth
              size="small"
            >
              <MenuItem value="">All Categories</MenuItem>
              <MenuItem value="breakfast">Breakfast</MenuItem>
              <MenuItem value="lunch">Lunch</MenuItem>
              <MenuItem value="dinner">Dinner</MenuItem>
              <MenuItem value="snack">Snack</MenuItem>
              <MenuItem value="dessert">Dessert</MenuItem>
            </TextField> */}
            <TextField
              select
              label="Sort By"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              fullWidth
              size="small"
            >
              <MenuItem value="serialNumber">Serial Number</MenuItem>
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="category">Category</MenuItem>
              <MenuItem value="rating">Rating</MenuItem>
            </TextField>
            <TextField
              select
              label="Sort Order"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as "asc" | "desc")}
              fullWidth
              size="small"
            >
              <MenuItem value="asc">Ascending</MenuItem>
              <MenuItem value="desc">Descending</MenuItem>
            </TextField>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Button
                variant="outlined"
                onClick={handleResetFilters}
                startIcon={<ClearIcon />}
                fullWidth
              >
                Clear Filters
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Meals Grid */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
            <CircularProgress />
          </Box>
        ) : filteredMeals.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <RestaurantIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No meals found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your search criteria or filters
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 3 }}>
            {filteredMeals.map((meal) => (
              <Box key={meal._id}>
                <Card 
                  sx={{ height: "100%", display: "flex", flexDirection: "column", cursor: "pointer", transition: "transform 0.2s", "&:hover": { transform: "scale(1.02)" } }}
                  onClick={() => {
                    setSelectedMealId(meal._id);
                    setModalOpen(true);
                  }}
                >
                  {/* Meal Image */}
                  <CardMedia
                    component="img"
                    height="200"
                    image={meal.imageUrl || "https://via.placeholder.com/300x200?text=No+Image"}
                    alt={meal.name}
                    sx={{ objectFit: "cover" }}
                  />
                  
                  <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
                    {/* Serial Number Badge */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                      <Badge
                        badgeContent={meal.serialNumber}
                        color="primary"
                        sx={{
                          "& .MuiBadge-badge": {
                            fontSize: "0.75rem",
                            fontWeight: "bold",
                            backgroundColor: "primary.main",
                          },
                        }}
                      >
                        <Box sx={{ width: 8, height: 8 }} />
                      </Badge>
                      <Chip
                        label={meal.category}
                        color={getCategoryColor(meal.category)}
                        size="small"
                        icon={<TagIcon />}
                      />
                    </Box>

                    {/* Meal Name */}
                    <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: "bold" }}>
                      {meal.name}
                    </Typography>

                    {/* Rating */}
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Rating value={meal.rating} readOnly size="small" />
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        ({meal.noOfRatings})
                      </Typography>
                    </Box>

                    {/* Description */}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
                      {meal.description.length > 100 
                        ? `${meal.description.substring(0, 100)}...` 
                        : meal.description}
                    </Typography>

                    {/* Nutritional Info */}
                    {meal.nutritionalInfo && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: "bold" }}>
                          Nutrition:
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {formatNutritionalInfo(meal.nutritionalInfo)}
                        </Typography>
                      </Box>
                    )}

                    {/* Portion Size */}
                    {meal.portionSize && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: "bold" }}>
                          Portion:
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {meal.portionSize}
                        </Typography>
                      </Box>
                    )}

                    {/* Allergens */}
                    {meal.allergens && meal.allergens.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: "bold" }}>
                          Allergens:
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                          {meal.allergens.slice(0, 3).map((allergen, index) => (
                            <Chip
                              key={index}
                              label={allergen}
                              size="small"
                              variant="outlined"
                              color="warning"
                            />
                          ))}
                          {meal.allergens.length > 3 && (
                            <Chip
                              label={`+${meal.allergens.length - 3} more`}
                              size="small"
                              variant="outlined"
                              color="default"
                            />
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* <Divider sx={{ my: 1 }} /> */}

                                        {/* Actions */}
                    {/* <Box sx={{ display: "flex", gap: 1, mt: "auto" }}>
                      <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        startIcon={<InfoIcon />}
                      >
                        Details
                      </Button>
                    </Box> */}
                   </CardContent>
                 </Card>
               </Box>
             ))}
           </Box>
        )}

        {/* Total Count Info */}
        {!loading && filteredMeals.length > 0 && (
          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Showing {filteredMeals.length} meal{filteredMeals.length === 1 ? "" : "s"}
            </Typography>
          </Box>
        )}
      </Box>

      <MealDetailModal 
        open={modalOpen} 
        onClose={() => setModalOpen(false)} 
        mealId={selectedMealId} 
        onUpdate={fetchMeals} 
      />
    </Layout>
  );
};

export default Meals; 